import React from 'react';
import { FieldsNotNull, filterWhereFieldsNotNull } from '@/lib/utils/typeGuardUtils';
import { defineStyles, useStyles } from '../hooks/useStyles';
import { DocumentContributorWithStats, DocumentContributorsInfo } from '@/lib/arbital/useTagLenses';
import UsersNameDisplay from "../users/UsersNameDisplay";
import LWTooltip from "../common/LWTooltip";

const styles = defineStyles("ContributorsList", (theme: ThemeType) => ({
  contributorNameWrapper: {
    display: 'inline',
    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },
  contributorName: {
    fontWeight: 550,
  },
  tocContributors: {
    marginBottom: 12,
    marginLeft: 16,
  },
  tocContributor: {
    cursor: "pointer",
    fontFamily: theme.palette.fonts.sansSerifStack,
    color: theme.palette.greyAlpha(0.5),
    fontWeight: 550,
  },
}))

type NonnullDocumentContributorWithStats = FieldsNotNull<DocumentContributorWithStats, 'user'>

export function useDisplayedContributors(contributorsInfo: DocumentContributorsInfo | null) {
  const contributors = filterWhereFieldsNotNull(contributorsInfo?.contributors ?? [], 'user');
  if (!contributors.some(({ currentAttributionCharCount }) => currentAttributionCharCount)) {
    return { topContributors: contributors, smallContributors: [] };
  }

  const totalAttributionChars = contributors.reduce((acc: number, contributor: DocumentContributorWithStats) => acc + (contributor.currentAttributionCharCount ?? 0), 0);

  if (totalAttributionChars === 0) {
    return { topContributors: contributors, smallContributors: [] };
  }

  const sortedContributors = [...contributors].sort((a, b) => (b.currentAttributionCharCount ?? 0) - (a.currentAttributionCharCount ?? 0));
  const initialTopContributors = sortedContributors.filter(({ currentAttributionCharCount }) => ((currentAttributionCharCount ?? 0) / totalAttributionChars) > 0.1);
  const topContributors = initialTopContributors.length <= 3 
    ? sortedContributors.filter(({ currentAttributionCharCount }) => ((currentAttributionCharCount ?? 0) / totalAttributionChars) > 0.05)
    : initialTopContributors;
  const smallContributors = sortedContributors.filter(contributor => !topContributors.includes(contributor));

  return { topContributors, smallContributors };
}

const ContributorsList = ({ contributors, onHoverContributor, endWithComma }: {
  contributors: NonnullDocumentContributorWithStats[],
  onHoverContributor: (userId: string|null) => void,
  endWithComma: boolean
}) => {
  const classes = useStyles(styles);

  return <>{contributors.map(({ user }, idx) => (<span key={user._id} onMouseOver={() => onHoverContributor(user._id)} onMouseOut={() => onHoverContributor(null)}>
    <UsersNameDisplay user={user} noTooltip={true} className={classes.contributorName} />
    {endWithComma || idx < contributors.length - 1 ? ', ' : ''}
  </span>))}</>;
}

export function ToCContributorsList({
  contributors,
  onHoverContributor,
}: {
  contributors: NonnullDocumentContributorWithStats[]
  onHoverContributor: (userId: string | null) => void
}) {
  const classes = useStyles(styles);

  const displayedContributors = contributors.slice(0, 2);
  const hiddenContributors = contributors.slice(2);

  return (
    <div className={classes.tocContributors}>
      {displayedContributors.map(({ user }, idx) => (
        <span key={user._id} className={classes.tocContributor} onMouseOver={() => onHoverContributor(user._id)} onMouseOut={() => onHoverContributor(null)}>
          <UsersNameDisplay user={user} noTooltip={true} className={classes.contributorName} />
          {(idx < displayedContributors.length - 1) || (idx === displayedContributors.length - 1 && hiddenContributors.length > 0) ? ', ' : ''}
        </span>
      ))}
      {hiddenContributors.length > 0 && (
        <LWTooltip
          title={hiddenContributors.map(( {user}, idx) => (
            <span key={user._id} onMouseOver={() => onHoverContributor(user._id)} onMouseOut={() => onHoverContributor(null)}>
              <UsersNameDisplay user={user} noTooltip={true} className={classes.contributorName} />
              {idx < hiddenContributors.length - 1 ? ', ' : ''}
            </span>
          ))}
          clickable
          placement="top"
        >
          <span className={classes.tocContributor}>et al.</span>
        </LWTooltip>
      )}
    </div>
  );
}

export const HeadingContributorsList = ({topContributors, smallContributors, onHoverContributor}: {
  topContributors: NonnullDocumentContributorWithStats[]
  smallContributors: NonnullDocumentContributorWithStats[]
  onHoverContributor: (userId: string|null) => void
}) => {
  const classes = useStyles(styles);
  return <span className={classes.contributorNameWrapper}>
    <span>Edited by </span>
    <ContributorsList
      contributors={topContributors}
      onHoverContributor={onHoverContributor}
      endWithComma={smallContributors.length > 0}
    />
    {smallContributors.length > 0 && <LWTooltip
      title={<ContributorsList contributors={smallContributors} onHoverContributor={onHoverContributor} endWithComma={false} />}
      clickable
      placement="top"
    >
      et al.
    </LWTooltip>}
  </span>
}
